import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { kycAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

export default function KYCPage() {
  const { refreshUser } = useAuth();
  const { t } = useLanguage();
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [docType, setDocType] = useState('id_card');

  const loadKYC = useCallback(async () => {
    try { const res = await kycAPI.getStatus(); setKyc(res.data); } catch (err) { console.error('KYC load error:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadKYC(); }, [loadKYC]);

  const handleSubmit = async () => {
    if (!fullName || !idNumber) { toast.error(t('kyc.fillRequired')); return; }
    setSubmitLoading(true);
    try {
      await kycAPI.submit({ full_name: fullName, id_number: idNumber, phone, document_type: docType });
      toast.success(t('kyc.submitted'));
      await loadKYC(); await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.submitFailed'));
    } finally { setSubmitLoading(false); }
  };

  const statusConfig = {
    approved: { icon: CheckCircle2, color: 'text-[#2DD36F]', bg: 'bg-[rgba(45,211,111,0.12)]', border: 'border-[#2DD36F]/20', label: t('kyc.approved'), progress: 100 },
    pending: { icon: Clock, color: 'text-[#F7C84B]', bg: 'bg-[rgba(247,200,75,0.12)]', border: 'border-[#F7C84B]/20', label: t('kyc.pending'), progress: 50 },
    rejected: { icon: XCircle, color: 'text-[#D94A4A]', bg: 'bg-[rgba(217,74,74,0.12)]', border: 'border-[#D94A4A]/20', label: t('kyc.rejected'), progress: 0 },
    none: { icon: ShieldCheck, color: 'text-[hsl(var(--muted-foreground))]', bg: 'bg-[hsl(var(--secondary))]', border: 'border-[hsl(var(--border))]', label: t('kyc.notStarted'), progress: 0 },
  };

  const status = kyc?.status || 'none';
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <DashboardLayout title={t('kyc.title')}>
      <div className="max-w-2xl space-y-6">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <StatusIcon className={`w-6 h-6 ${config.color}`} />
                <span className="font-semibold font-['Space_Grotesk'] text-lg">{t('kyc.statusTitle')}</span>
              </div>
              <Badge data-testid="kyc-status-badge" className={`${config.bg} ${config.color} ${config.border}`}>{config.label}</Badge>
            </div>
            <Progress value={config.progress} className="h-2 mb-3" />
            <div className="flex items-center gap-8 text-sm text-[hsl(var(--muted-foreground))]">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${status !== 'none' ? 'bg-[#2DD36F]' : 'bg-[hsl(var(--muted-foreground))]'}`} />
                {t('kyc.application')}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${status === 'pending' ? 'bg-[#F7C84B]' : status === 'approved' ? 'bg-[#2DD36F]' : 'bg-[hsl(var(--muted-foreground))]'}`} />
                {t('kyc.review')}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${status === 'approved' ? 'bg-[#2DD36F]' : 'bg-[hsl(var(--muted-foreground))]'}`} />
                {t('kyc.approval')}
              </div>
            </div>
          </CardContent>
        </Card>

        {status === 'rejected' && kyc?.admin_notes && (
          <Alert className="bg-[rgba(217,74,74,0.08)] border-[#D94A4A]/20">
            <XCircle className="w-4 h-4 text-[#D94A4A]" />
            <AlertDescription className="text-[#D94A4A]">
              <p className="font-medium mb-1">{t('kyc.rejectReason')}:</p>
              <p>{kyc.admin_notes}</p>
            </AlertDescription>
          </Alert>
        )}

        {(status === 'none' || status === 'rejected') && (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('kyc.formTitle')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('kyc.fullName')} *</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t('kyc.fullNamePlaceholder')} required className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc.idNumber')} *</Label>
                <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder={t('kyc.idNumberPlaceholder')} required className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc.phone')} ({t('common.optional')})</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('kyc.phonePlaceholder')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
              </div>
              <div className="space-y-2">
                <Label>{t('kyc.docType')}</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    <SelectItem value="id_card">{t('kyc.idCard')}</SelectItem>
                    <SelectItem value="passport">{t('kyc.passport')}</SelectItem>
                    <SelectItem value="drivers_license">{t('kyc.driversLicense')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Alert className="bg-[rgba(18,200,178,0.08)] border-[hsl(var(--primary))]/20">
                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                <AlertDescription className="text-sm">{t('kyc.securityNote')}</AlertDescription>
              </Alert>
              <Button data-testid="kyc-submit-button" onClick={handleSubmit} disabled={submitLoading || !fullName || !idNumber} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11">
                {submitLoading ? t('kyc.submitting') : t('kyc.submitBtn')}
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'pending' && (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6 text-center">
              <Clock className="w-12 h-12 text-[#F7C84B] mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">{t('kyc.pendingTitle')}</h3>
              <p className="text-[hsl(var(--muted-foreground))]">{t('kyc.pendingDesc')}</p>
            </CardContent>
          </Card>
        )}

        {status === 'approved' && (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-[#2DD36F] mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">{t('kyc.approvedTitle')}</h3>
              <p className="text-[hsl(var(--muted-foreground))]">{t('kyc.approvedDesc')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
